import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import type { CsatCampaign } from '@/types/csat'
import type { Document } from '@/types/document'
import type { GamificationPage } from '@/types/gamificationPage'
import type { GamificationPageBlock } from '@/types/gamificationPageBlock'
import type { Journey, Challenge, Level, Stage } from '@/types/journey'
import type { Mission } from '@/types/mission'
import type { NetworkingTag } from '@/types/networking'
import type { Partner } from '@/types/partner'
import type { Prize } from '@/types/prizes'
import type { Quiz } from '@/types/quiz'

/**
 * As dezessete chaves que o servidor entrega em `/gamification/data` — nem uma a
 * mais.
 *
 * `guilds` foi declarado aqui por anos e **nunca foi enviado**: existia como
 * método privado do presenter, fora do hash. Um campo que o tipo promete e o
 * servidor não entrega é pior que campo ausente — o compilador garante que ele
 * existe, e em tempo de execução ele é `undefined`.
 *
 * `posts`, `programmings` e `faqs` seguem sem tipo de elemento de propósito:
 * nenhuma tela os lê do store — as que precisam desse conteúdo buscam por
 * REST. Inventar uma forma para eles seria documentar um contrato que ninguém
 * exercita, e que envelheceria sem nada acusar.
 *
 * `partners` teve o mesmo tratamento por anos — a mecanismo de liberação da
 * Lojinha por check-in de parceiro é o primeiro leitor real, então ganhou
 * tipo (`Partner[]`) em vez de `unknown[]`.
 */
export type GamificationData = {
  version: string

  journeys: Journey[]
  stages: Stage[]
  levels: Level[]
  challenges: Challenge[]

  missions: Mission[]
  quizzes: Quiz[]
  prizes: Prize[]
  posts: unknown[]
  programmings: unknown[]
  documents: Document[]
  faqs: unknown[]
  partners: Partner[]
  gamification_pages: GamificationPage[]
  csat_campaigns: CsatCampaign[]
  networking_tags: NetworkingTag[]
  /** Blocos ativos da home, na ordem definida pelo produtor. */
  home_blocks: GamificationPageBlock[]
}

export const EMPTY_GAMIFICATION_DATA: GamificationData = {
  version: '',
  journeys: [],
  stages: [],
  levels: [],
  challenges: [],
  missions: [],
  quizzes: [],
  prizes: [],
  posts: [],
  programmings: [],
  documents: [],
  faqs: [],
  partners: [],
  gamification_pages: [],
  csat_campaigns: [],
  networking_tags: [],
  home_blocks: []
} as const

interface GamificationDataState {
  dataByProductId: {
    [key: string]: GamificationData | undefined
  }
  activeDigitalProductId: string | null

  setActiveDigitalProductId: (id: string | null) => void
  setProductData: (id: string, data: GamificationData) => void

  getActiveProductData: () => GamificationData
}

export const useGamificationDataStore = create(
  persist<GamificationDataState>(
    (set, get) => ({
      dataByProductId: {},
      activeDigitalProductId: null,

      setActiveDigitalProductId: id =>
        set(state => {
          const newState = { activeDigitalProductId: id }

          if (id && !state.dataByProductId[id]) {
            return {
              ...newState,
              dataByProductId: {
                ...state.dataByProductId,
                [id]: EMPTY_GAMIFICATION_DATA
              }
            }
          }

          return newState
        }),

      setProductData: (id, data) =>
        set(state => ({
          dataByProductId: {
            ...state.dataByProductId,
            [id]: data
          }
        })),

      getActiveProductData: () => {
        const state = get()
        const activeId = state.activeDigitalProductId
        return activeId
          ? state.dataByProductId[activeId] ?? EMPTY_GAMIFICATION_DATA
          : EMPTY_GAMIFICATION_DATA
      }
    }),
    {
      name: 'evnttz.gamification-data',
      storage: createJSONStorage(() => localStorage)
    }
  )
)
